// Ticket selection: turns one day's head-to-head odds into four multis.
// These rules are the product. Change them here and in IDEATION.md together.

// The four designs, and the four words a ticket can print. They are the same
// four strings and they are no longer the same idea: `TIER_ORDER` names the
// DESIGNS a Classic pad deals — the leg counts, which is what the ids, the
// caps and the Settings tick-boxes are keyed by — while the word printed on a
// ticket comes from `tierFor(combinedProbability)` and can be any of the four.
// A two-leg design landing at 36% prints Medium risk, and that is the honest
// answer: see lib/tier.js.
export { TIER_ORDER } from './tier.js';
import { TIER_ORDER, tierFor } from './tier.js';
// `bookmakers.js` is a leaf — it imports nothing, its own header says why — so
// this edge cannot cycle. It is here for `legLink`: a leg built from a board
// match carries the link the book that quoted it published, and the rule for
// what counts as one lives beside the addresses rather than in two places.
import { legLink } from './bookmakers.js';
// The no-value glyph, from the one place it is typed. `format.js` is a leaf
// like the two above, so this edge cannot cycle either.
import { NO_VALUE } from './format.js';

// EVERY LEG IS A FAVOURITE, IN EVERY DESIGN, AND THE DESIGN IS THE LEG COUNT.
//
// There is no probability band, no search target and no composition rule. An
// earlier version had all three: each tier aimed at a combined probability, and
// reached for coin flips and underdogs to get there, so high and outrageous
// bought their long prices with legs nobody would back on their own. Two
// favourites land more often than not; eight favourites is a lottery, and it is
// a lottery made of the same legs.
//
// A design takes as many legs as the board allows, up to `maxLegs`, and prints
// short down to `minLegs`. Below `minLegs` the design is void.
//
// THE KEYS ARE DESIGN IDS, not the risk printed on the ticket. They read like
// tiers because they always have — every stored ticket id, every prefs entry
// and every proposal in the paper trail is keyed on these four strings, and
// renaming them would orphan the record. What changed is what they MEAN.
export const TIERS = {
  low: { minLegs: 2, maxLegs: 2 },
  medium: { minLegs: 3, maxLegs: 3 },
  high: { minLegs: 4, maxLegs: 5 },
  outrageous: { minLegs: 6, maxLegs: 8 },
};

/** What each design is called when there is no chance to print instead. */
export const DESIGN_LABEL = {
  low: 'Two legs',
  medium: 'Three legs',
  high: 'Four or five legs',
  outrageous: 'Six to eight legs',
};

export const FAVOURITE = 0.6; // p >= FAVOURITE is a favourite — about $1.67 or shorter

// THE ONE DIAL ON THE TIER SEARCH: how many of a tier's legs come out of the
// long band instead of the favourites pool.
//
// A long shot REPLACES a favourite; it is never an extra leg. The tier is
// still a leg count — low is two legs with two long shots as surely as it is
// two legs with none — so turning this up lengthens every ticket without
// touching the shape of the pad. Plain Classic is this at zero, which is why
// zero has to be the default and has to be byte-for-byte the favourites-only
// pad.
//
// Three is the ceiling because the medium tier is three legs: a fourth would
// leave a tier with no favourite on it at all, which is a different product.
export const MAX_LONG_SHOTS = 3;

/** Where a long shot is priced by default, in decimal odds. */
export const LONG_BAND = [2.0, 4.0];

const isFavourite = (c) => c.p >= FAVOURITE;

/** A decimal price inside a [lo, hi] band, both ends included. */
const inBand = (price, [lo, hi]) => price >= lo && price <= hi;

/**
 * A long shot: priced inside the band AND not a favourite.
 *
 * The second half is not redundant. `validateStrategy` keeps a stored band
 * above the favourite price, but `generateTickets` is a function anybody can
 * call with any band, and a leg that counted as both would be picked twice —
 * once as the long shot the ticket asked for and once as the favourite meant
 * to balance it.
 */
const isLongShot = (c, band) => !isFavourite(c) && inBand(c.price, band);

/** YYYY-MM-DD for a moment, in the given zone (device zone when omitted). */
export function localDate(when, timeZone) {
  return new Date(when).toLocaleDateString('en-CA', { timeZone });
}

/**
 * The Odds API v4 response → odds-event records for one bookmaker.
 * Events the bookmaker has not priced are dropped.
 */
export function normaliseOdds(apiEvents, bookmakerKey, fetchedAt) {
  const out = [];
  for (const e of apiEvents) {
    const book = e.bookmakers?.find((b) => b.key === bookmakerKey);
    const h2h = book?.markets?.find((m) => m.key === 'h2h');
    if (!h2h) continue;
    const price = (name) => h2h.outcomes.find((o) => o.name === name)?.price ?? null;
    out.push({
      id: e.id,
      sportKey: e.sport_key,
      sportTitle: e.sport_title,
      homeTeam: e.home_team,
      awayTeam: e.away_team,
      commenceTime: e.commence_time,
      bookmaker: book.title,
      homePrice: price(e.home_team),
      awayPrice: price(e.away_team),
      drawPrice: price('Draw'),
      fetchedAt,
    });
  }
  return out;
}

/**
 * Every side of every eligible event, as a candidate leg. Eligible means it
 * starts later on `forDate` than `asOf` and has a usable price. Draws are
 * not legs in v1. `sameDay: false` drops the date test (the /demo sample day,
 * which is built relative to now and may run past midnight).
 */
export function buildPool(events, { forDate, asOf, timeZone, sameDay = true }) {
  const cutoff = new Date(asOf).getTime();
  const pool = [];
  for (const e of events) {
    const start = new Date(e.commenceTime).getTime();
    if (!(start > cutoff)) continue;
    if (sameDay && localDate(start, timeZone) !== forDate) continue;
    for (const [side, team, price] of [
      ['home', e.homeTeam, e.homePrice],
      ['away', e.awayTeam, e.awayPrice],
    ]) {
      if (!Number.isFinite(price) || price <= 1) continue;
      pool.push({
        id: `${e.id}:${side}`,
        eventId: e.id,
        selection: `${team} to win`,
        match: `${e.homeTeam} v ${e.awayTeam}`,
        sportTitle: e.sportTitle,
        commenceTime: e.commenceTime,
        price,
        p: 1 / price,
      });
    }
  }
  return pool;
}

// FNV-1a: a stable tie-break so the same day always sorts the same way.
function hash(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const product = (legs, key) => legs.reduce((acc, l) => acc * l[key], 1);

/**
 * The legs for one tier, or null when today's board has too few of them.
 *
 * With `longShots: 0` — plain Classic — every candidate is a favourite, so
 * which favourites is the only question left, and the answer is the same
 * seeded hash that used to break ties in the old search: deal the favourites,
 * then take them in that order, one per match, until the tier is full. Same
 * day and same seed, same ticket; "Another set" bumps the seed and deals a
 * different one.
 *
 * The old search ranked candidates by how near they were to a per-leg target,
 * and the seed only broke ties between equals. That machinery has nothing left
 * to aim at — a tier is a leg count now — and the one thing it could still be
 * pointed at, "as likely as possible", would print the N shortest favourites
 * for every seed, so "Another set" would deal the same pad every time. The
 * bias toward the likely is the `FAVOURITE` floor itself: nothing longer than
 * about $1.67 is on any ticket.
 *
 * WITH LONG SHOTS IT IS TWO DEALS OFF ONE SHUFFLE. `k` legs are taken out of
 * the long band first, then the tier is filled to `maxLegs` out of the
 * favourites — so k long shots and maxLegs − k favourites, from distinct
 * matches across both passes. The ceiling never moves, which is the whole
 * idea: a long shot costs a favourite, it does not buy an extra leg. A board
 * with fewer long shots than asked for is filled up with favourites rather
 * than printed short; `generateTickets` counts what it found and says so.
 */
export function pickLegs(tier, pool, forDate, seed = 0, { longShots = 0, longBand = LONG_BAND } = {}) {
  const { minLegs, maxLegs } = TIERS[tier];
  // One seeded order, read twice. The id tie-break makes it total, so the
  // input order of the day's events cannot change the ticket even if two
  // hashes collide.
  const deal = (list) => list
    .map((c) => [hash(`${forDate}|${tier}|${seed}|${c.id}`), c])
    .sort((a, b) => a[0] - b[0] || a[1].id.localeCompare(b[1].id))
    .map(([, c]) => c);

  const legs = [];
  const matches = new Set();
  const take = (candidates, upTo) => {
    for (const c of candidates) {
      if (legs.length >= upTo) break;
      if (matches.has(c.eventId)) continue;
      matches.add(c.eventId);
      legs.push(c);
    }
  };

  const wanted = Math.min(Math.max(0, Math.trunc(longShots) || 0), maxLegs);
  if (wanted) take(deal(pool.filter((c) => isLongShot(c, longBand))), wanted);
  take(deal(pool.filter(isFavourite)), maxLegs);
  return legs.length >= minLegs ? legs : null;
}

const cents = (n) => Math.round(n * 100) / 100;

/**
 * Today's tickets. Same events + same forDate + same asOf = same tickets.
 * Returns one { multi, legs } per tier in `tiers`, in tier order.
 *
 * `asOf` IS THE CUTOFF, NOT THE PRICE STAMP. It is the moment the deal is being
 * cut at — nothing starting before it is eligible — and a set dealt in the
 * afternoon is cut at the afternoon while its prices are still the morning's.
 * `oddsFetchedAt` is when those prices were read, which is what goes on the
 * ticket and what the STALE mark is measured from; left out, the two are the
 * same moment, which is the pad on a board just fetched.
 *
 * `longShots` (0..3) and `longBand` are the tier search's only dial — see
 * `MAX_LONG_SHOTS` and `pickLegs`. At zero this prints exactly what it printed
 * before the dial existed, down to the keys on the multi: a ticket with no
 * long shots on it does not carry a count of them.
 */
export function generateTickets({
  events,
  forDate,
  asOf,
  oddsFetchedAt: pricedAt,
  stake,
  bookmaker,
  tiers = TIER_ORDER,
  timeZone,
  sameDay = true,
  seed = 0,
  longShots = 0,
  longBand = LONG_BAND,
}) {
  const pool = buildPool(events, { forDate, asOf, timeZone, sameDay });
  const oddsFetchedAt = new Date(pricedAt ?? asOf).toISOString();
  // Clamped rather than trusted: this is a public function, and a tier with
  // more long shots than legs is not a ticket anybody asked for.
  const k = Math.min(Math.max(0, Math.trunc(Number(longShots)) || 0), MAX_LONG_SHOTS);
  return TIER_ORDER.filter((t) => tiers.includes(t)).map((tier) => {
    const id = seed ? `${forDate}-${tier}-s${seed}` : `${forDate}-${tier}`;
    const picked = pickLegs(tier, pool, forDate, seed, { longShots: k, longBand });
    if (!picked) {
      return {
        multi: {
          id,
          // The DESIGN that could not be filled, and no tier at all: a ticket
          // with no legs has no chance, and a colour chosen for it would be a
          // reading of a number that does not exist. The screen prints the
          // design's own name (`DESIGN_LABEL`) where the risk would have gone.
          design: tier,
          tier: null,
          forDate,
          status: 'void',
          stake,
          bookmaker,
          oddsFetchedAt,
          combinedOdds: null,
          combinedProbability: null,
          potentialReturn: null,
          // Why this tier is void, in the tier's own terms. The reader is owed
          // the number: "too few" is not an answer when the pad next to it
          // printed three legs from the same board. With long shots turned on
          // the pool is favourites AND long shots, so naming only the
          // favourites would be the wrong count to go and check.
          note: k
            ? `Not enough legs on today’s board for a ${TIERS[tier].minLegs}-leg ticket.`
            : `Not enough favourites on today’s board for ${TIERS[tier].minLegs} legs.`,
        },
        legs: [],
      };
    }
    // Printed ticket order: earliest start first.
    const ordered = picked
      .slice()
      .sort((a, b) => a.commenceTime.localeCompare(b.commenceTime) || a.id.localeCompare(b.id));
    const combinedOdds = cents(product(ordered, 'price'));
    const combinedProbability = product(ordered, 'p');
    return {
      multi: {
        id,
        // WHAT WAS DEALT and WHAT IT IS are two different fields now. The
        // design is the leg count this ticket was cut to — it keys the id, the
        // cap and the tick-box — and the tier is the band its combined chance
        // actually falls in, which on a thin board is often not the one the
        // design would have guessed. `tierFor` is the only writer of a tier
        // string in the app; see lib/tier.js.
        //
        // The chance here is the legs' own implied probabilities multiplied
        // out — the book's price with its margin still in it, which is the
        // only reading a Classic ticket has. It is therefore the OPTIMISTIC
        // one, and the ticket's separate `ev`/`fairProbability` fields (set in
        // strategies/classic.js, off the devigged prices) are where the honest
        // reading of what it is worth lives. One field for the risk, one for
        // the value, and neither speaking for the other.
        design: tier,
        tier: tierFor(combinedProbability),
        forDate,
        status: 'proposed',
        stake,
        bookmaker,
        oddsFetchedAt,
        combinedOdds,
        combinedProbability,
        potentialReturn: cents(stake * combinedOdds),
        // WHAT WAS ASKED FOR AND WHAT THE BOARD HAD. Counted off the printed
        // legs, not off the search, because that is the number a reader can
        // check against the ticket in front of them. Only where a long shot
        // was asked for at all: a favourites-only ticket carrying "0 of 0"
        // would be a field about a feature nobody turned on.
        ...(k ? { longShots: k, longShotsFound: ordered.filter((c) => isLongShot(c, longBand)).length } : {}),
      },
      legs: ordered.map((c) => ({
        id: `${id}-${c.eventId}`,
        multiId: id,
        eventId: c.eventId,
        selection: c.selection,
        match: c.match,
        sportTitle: c.sportTitle,
        commenceTime: c.commenceTime,
        price: c.price,
        impliedProbability: c.p,
      })),
    };
  });
}

// --- a bet the app never printed ---------------------------------------------

/** What a hand-logged ticket calls itself, for ever. One spelling, two readers. */
export const HAND_LABEL = 'Logged by hand';

/**
 * ONE LEG, OFF A MATCH ON THE BOARD — the shape every leg in this app carries,
 * written down once.
 *
 * Two things build a leg from a board match rather than from a strategy run: a
 * bet logged by hand (`handTicket` below) and a leg added to a ticket
 * (`engine/reprice.js`). They must produce the same object, because everything
 * downstream — settlement by `matchKey`, the live price by `side`, the printed
 * "Collingwood to win" — reads the same fields whichever way the leg got there.
 * Two copies of this would be two subtly different legs in one ledger.
 *
 * `id` and `multiId` are the CALLER's: a leg is named after the ticket it is
 * on, and this function does not know which ticket that is.
 *
 * The engine writes every selection as "<team> to win"; a draw is its own noun
 * and has nothing to win. `side` beside it is the machine-readable half that
 * settlement compares — the text is for the punter.
 */
export function legFromMatch(match, side, { price, bookmakerKey = null, fairProbability = null } = {}) {
  // A RUNNER, WHERE THE MATCH HAS RUNNERS. "Winx (4) to win" on "Randwick R5",
  // which is the same two strings the engine's own `describe` writes — the
  // saddlecloth rides with the name because two horses in one spring carnival
  // can share one, and the number is what the bookmaker's own slip is keyed on.
  // `side` is already the runner id (`r4`), so settlement compares the same
  // word here as it does on a leg the engine picked.
  const runner = match?.runners?.[side];
  if (runner) {
    const odds = cents(Number(price));
    return {
      matchKey: match?.matchKey ?? null,
      eventId: match?.matchKey ?? null,
      // THE PROVIDER'S OWN ID FOR THE EVENT, where the board had one. It is
      // what settlement joins on first (`rowsFor`, stores/settlement.js),
      // because it is the one identifier that is not re-derived at both ends
      // and therefore cannot be derived two different ways. Null where the
      // match came from somewhere that does not publish one, and the key is
      // the answer again.
      providerEventId: match?.providerEventId ?? null,
      side,
      selection: runner.number === null || runner.number === undefined
        ? `${runner.name} to win`
        : `${runner.name} (${runner.number}) to win`,
      match: match?.raceLabel ?? `${match?.venue ?? ''} R${match?.raceNumber ?? ''}`,
      sport: match?.sport ?? null,
      sportTitle: match?.sportTitle ?? match?.competition ?? null,
      commenceTime: match?.kickoff ?? null,
      price: odds,
      impliedProbability: 1 / odds,
      fairProbability,
      bookmakerKey,
      // THE BOOK'S OWN PAGE FOR THIS BET, where the row that priced it carried
      // one. Read off the board rather than passed in, so every caller gets it
      // without knowing it exists, and read by `bookmakerKey` so the link is
      // the link of the book this leg is actually at. Absent where the row had
      // none, which is what every leg looked like until now.
      ...legLink(match, side, bookmakerKey),
      // The card's own word for "it has jumped" — `ticketState` reads it off
      // the leg, and a leg built here is a leg like any other.
      ...(match?.inplayAt ? { inplayAt: match.inplayAt } : {}),
      ...(match?.marketClosedAt ? { marketClosedAt: match.marketClosedAt } : {}),
    };
  }

  const name = side === 'draw' ? 'Draw' : (match?.[side]?.name ?? '');
  const odds = cents(Number(price));
  return {
    matchKey: match?.matchKey ?? null,
    eventId: match?.matchKey ?? null,
    // See the runner branch: the provider's id is what settlement joins on
    // first, and it is the half of a leg's identity nobody has to re-derive.
    providerEventId: match?.providerEventId ?? null,
    side,
    selection: side === 'draw' ? 'Draw' : `${name} to win`,
    match: `${match?.home?.name ?? ''} v ${match?.away?.name ?? ''}`,
    sport: match?.sport ?? null,
    sportTitle: match?.competition ?? null,
    // `commenceTime` and not `kickoff`: it is what `resolveLeg` dates a leg by
    // and what every other leg in the app carries. Same mapping the engine
    // makes off its own candidates.
    commenceTime: match?.kickoff ?? null,
    price: odds,
    impliedProbability: 1 / odds,
    fairProbability,
    bookmakerKey,
    // See the runner branch above: the leg carries whatever link the book that
    // priced it published, and nothing else.
    ...legLink(match, side, bookmakerKey),
  };
}

/**
 * A bet somebody placed themselves, shaped as a ticket the ledger can hold.
 *
 * THE WAY IN IS THE MATCH PAGE, and that is the whole of the design. A leg
 * built from a match on the board carries the board's own `matchKey` and its
 * `providerEventId`, which are the two fields `resolveLeg` looks a result row up
 * by (the id first) — so this settles through exactly the path a ticket the app
 * printed settles through, and the morning cron cannot tell the two apart. A
 * slip typed in from nothing would settle from nothing, which is why there is no
 * free-text way in.
 *
 * Shaped for `markPlaced`: `status: 'proposed'` so it is accepted, and the
 * `stake`, `combinedOdds`, `potentialReturn` and `bookmaker` the bet record it
 * opens is built from. `strategyId: null` with a frozen `label` is what History
 * and Insights read — both already tolerate a ticket no strategy printed, and
 * the label is what the row is named after.
 *
 * `bookmaker` is the book's display NAME and is passed in rather than looked
 * up: this file imports nothing and is going to stay that way (see
 * `slipMoney`). `fairProbability` is the board's own fair price for the side at
 * the moment it was logged, turned back into a probability — the caller reads
 * it off `matchSheet`, because the arithmetic for it lives in board-view.js.
 *
 * THROWS rather than answering null on a price or a stake that is not one. The
 * sheet checks both fields first, so nothing here should ever be seen; but a
 * refusal that came back as a ticket would reach `markPlaced` as a bet, and the
 * punter would be told a bet was recorded at $0.
 */
export function handTicket({
  match,
  side,
  price,
  bookmakerKey = null,
  bookmaker = null,
  stake,
  fairProbability = null,
  timeZone,
  now = Date.now(),
}) {
  const matchKey = match?.matchKey ?? null;
  if (!matchKey || !side) throw new Error('That match is no longer on the board.');
  const odds = Number(price);
  if (!Number.isFinite(odds) || odds <= 1) throw new Error('Enter the price you took, like 1.85.');
  const staked = Number(stake);
  if (!Number.isFinite(staked) || staked <= 0) throw new Error('Enter what you put on, like 10.');

  const combinedOdds = cents(odds);
  const amount = cents(staked);
  const id = `hand-${matchKey}-${side}-${now}`;
  const kickoff = match?.kickoff ?? null;

  return {
    multi: {
      id,
      tier: null,
      // The match's own calendar day, not the day it was typed in: a Saturday
      // night game logged after midnight belongs to the Saturday board.
      forDate: kickoff ? localDate(kickoff, timeZone) : localDate(now, timeZone),
      status: 'proposed',
      strategyId: null,
      label: HAND_LABEL,
      stake: amount,
      bookmaker,
      bookmakerKey,
      oddsFetchedAt: new Date(now).toISOString(),
      combinedOdds,
      combinedProbability: 1 / combinedOdds,
      potentialReturn: cents(amount * combinedOdds),
    },
    legs: [{
      id: `${id}-0`,
      multiId: id,
      ...legFromMatch(match, side, { price: combinedOdds, bookmakerKey, fairProbability }),
    }],
  };
}

/** "Collingwood to win @ 1.45", one per line — what Copy legs puts on the clipboard. */
export function legsAsText(legs) {
  return legs.map((l) => `${l.selection} @ ${l.price.toFixed(2)}`).join('\n');
}

// "$10", "$41.20" — the stake and the return, written the way a punter would
// write them on the back of an envelope rather than the way a column of figures
// has to line up. `format.js`'s `money` pads every figure to the cent, and
// "$10.00" in a one-line slip reads as a quote rather than the round ten
// dollars somebody actually put on.
//
// Not `limits.js`'s `dollars`, which is the same rule: limits.js imports THIS
// file, and reaching back would make a cycle out of two lines of arithmetic.
// This file imports leaves only, and is going to stay that way.
const slipMoney = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return NO_VALUE;
  return Number.isInteger(v) ? `$${v.toLocaleString('en-AU')}` : `$${v.toFixed(2)}`;
};

/** "Collingwood", off "Collingwood to win" — a bookmaker's slip names the team. */
const teamOf = (selection) => String(selection ?? '').replace(/\s+to win$/i, '');

/**
 * The whole ticket as text, for pasting into a bookmaker's own slip.
 *
 *   Collingwood v Carlton — Collingwood @ 1.85
 *   Geelong v Essendon — Geelong @ 2.23
 *   Multi @ 4.12 · $10 → $41.20
 *   Best at Neds
 *
 * `legsAsText` stays exactly as it is — it is what Copy legs has always put on
 * the clipboard, and somebody who taps Copy legs wants the legs. THIS is the
 * handoff: it names the MATCH beside every selection, because a bookmaker's
 * search box takes a team and a slip that says only "Collingwood" leaves the
 * punter working out which of two Collingwood markets they meant; and it
 * carries the stake and the return, because the first thing anyone does at the
 * other end is type the stake in and check the figure agrees.
 *
 * THE BOOK, only where the TICKET names one (`multi.bookmakerKey`). A single
 * priced at the one shop that is out of line says "Best at Neds"; a `best-book`
 * multi says "All legs at Neds", because the claim there is that the whole
 * ticket fits on one slip rather than that any leg is the longest on the board.
 * A multi at your own book names nothing: there is no such claim to make.
 */
export function slipText(multi, legs = []) {
  const rows = (legs ?? []).map((l) => {
    const pick = `${teamOf(l.selection)} @ ${Number(l.price).toFixed(2)}`;
    return l.match ? `${l.match} · ${pick}` : pick;
  });

  const odds = Number(multi?.combinedOdds);
  if (Number.isFinite(odds)) {
    // "Multi" on a multi and "Single" on a single: the Mispriced odds strategy
    // prints one-leg tickets, and a slip that called one of those a multi would
    // be the app misreading its own ticket out loud.
    const kind = (legs?.length ?? 0) === 1 ? 'Single' : 'Multi';
    rows.push(`${kind} @ ${odds.toFixed(2)} · ${slipMoney(multi.stake)} → ${slipMoney(multi.potentialReturn)}`);
  }

  if (multi?.bookmakerKey && multi.bookmaker) {
    const how = multi.bookmakerRule === 'best-book' ? 'All legs at' : 'Best at';
    rows.push(`${how} ${multi.bookmaker}`);
  }

  return rows.join('\n');
}
